import { useI18n } from '@ecom/i18n';
import { resolveErrorMessageKey } from '@ecom/i18n/errors';
import {
  Badge,
  Button,
  EmptyState,
  Field,
  Modal,
  Select,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeaderCell,
  TableRow,
  type BadgeTone,
} from '@ecom/ui-kit';
import { type ReactElement, useState } from 'react';

import {
  useDecidePromotionMutation,
  usePromotionsQuery,
  useRequestPromotionMutation,
} from '../api';
import {
  PROMOTION_STATUS_LABEL_KEY,
  type PlatformAccount,
  type SuperadminPromotion,
} from '../types';

import { ConfirmPasswordField } from '@/components/ui/ConfirmPasswordField';
import { TableSkeleton } from '@/components/ui/skeletons/TableSkeleton';
import { useToast } from '@/components/ui/toast/ToastProvider';
import { useSession } from '@/lib/session';

const STATUS_TONE: Record<SuperadminPromotion['status'], BadgeTone> = {
  pending: 'warn',
  confirmed: 'good',
  rejected: 'critical',
  cancelled: 'neutral',
  expired: 'neutral',
};

type Dialog =
  | { readonly kind: 'closed' }
  | { readonly kind: 'request' }
  | {
      readonly kind: 'decide';
      readonly promotion: SuperadminPromotion;
      readonly decision: 'confirm' | 'reject';
    };

const CLOSED: Dialog = { kind: 'closed' };

/**
 * The two-person promotion to superadmin. ONE superadmin requests the promotion of an active administrator; a DIFFERENT one confirms it.
 * The requester sees "waiting for another superadmin" instead of a Confirm button, and can cancel; everything that matters (never the
 * same person, never oneself, the cap of three, the expiry) is enforced by the database functions behind these buttons.
 */
export function PromotionsPanel({
  reason,
  accounts,
}: {
  readonly reason: string;
  readonly accounts: readonly PlatformAccount[];
}): ReactElement {
  const { t } = useI18n();
  const toast = useToast();
  const { userId } = useSession();
  const query = usePromotionsQuery(reason);
  const requestMutation = useRequestPromotionMutation();
  const decideMutation = useDecidePromotionMutation();

  const [dialog, setDialog] = useState<Dialog>(CLOSED);
  const [target, setTarget] = useState('');
  const [motive, setMotive] = useState('');
  const [password, setPassword] = useState('');
  const [formError, setFormError] = useState<string | null>(null);

  const candidates = accounts.filter(
    (account) => account.role === 'platform_admin' && account.status === 'active',
  );

  function open(next: Dialog): void {
    setFormError(null);
    setMotive('');
    setPassword('');
    setTarget('');
    setDialog(next);
  }

  function close(): void {
    setMotive('');
    setPassword('');
    setDialog(CLOSED);
  }

  async function onRequest(): Promise<void> {
    setFormError(null);
    try {
      await requestMutation.mutateAsync({
        userId: target,
        reason: motive,
        confirmPassword: password,
      });
      toast.success(t('admin.promotions.requestedToast'));
      close();
    } catch (error) {
      setFormError(t(resolveErrorMessageKey(error)));
      setPassword('');
    }
  }

  async function onDecide(): Promise<void> {
    if (dialog.kind !== 'decide') return;
    setFormError(null);
    const { promotion, decision } = dialog;
    try {
      await decideMutation.mutateAsync({
        id: promotion.id,
        decision,
        reason: motive,
        confirmPassword: password,
      });
      toast.success(
        decision === 'confirm'
          ? t('admin.promotions.confirmedToast')
          : promotion.requestedBy === userId
            ? t('admin.promotions.cancelledToast')
            : t('admin.promotions.rejectedToast'),
      );
      close();
    } catch (error) {
      setFormError(t(resolveErrorMessageKey(error)));
      setPassword('');
    }
  }

  const canSubmit =
    motive.trim().length >= 10 &&
    password.length > 0 &&
    (dialog.kind !== 'request' || target !== '');
  const promotions = query.data?.promotions ?? [];

  const decideTitle =
    dialog.kind === 'decide'
      ? dialog.decision === 'confirm'
        ? t('admin.promotions.confirmTitle')
        : dialog.promotion.requestedBy === userId
          ? t('admin.promotions.cancelTitle')
          : t('admin.promotions.rejectTitle')
      : '';

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <p className="max-w-xl text-sm text-inkFaint">{t('admin.promotions.subtitle')}</p>
        <Button
          onClick={() => {
            open({ kind: 'request' });
          }}
          disabled={candidates.length === 0}
        >
          {t('admin.promotions.requestTitle')}
        </Button>
      </div>
      {candidates.length === 0 ? (
        <p className="text-sm text-inkFaint">{t('admin.promotions.noCandidates')}</p>
      ) : null}

      <h3 className="font-display text-base font-semibold text-ink">
        {t('admin.promotions.listTitle')}
      </h3>
      {query.isPending ? (
        <TableSkeleton columns={5} />
      ) : query.isError ? (
        <EmptyState title={t('admin.accounts.loadError')} />
      ) : promotions.length === 0 ? (
        <EmptyState title={t('admin.promotions.empty')} />
      ) : (
        <Table caption={t('admin.promotions.table.caption')}>
          <TableHead>
            <TableRow>
              <TableHeaderCell>{t('admin.promotions.table.colTarget')}</TableHeaderCell>
              <TableHeaderCell>{t('admin.promotions.table.colRequestedBy')}</TableHeaderCell>
              <TableHeaderCell>{t('admin.promotions.table.colStatus')}</TableHeaderCell>
              <TableHeaderCell>{t('admin.promotions.table.colExpires')}</TableHeaderCell>
              <TableHeaderCell> </TableHeaderCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {promotions.map((promotion) => {
              const mine = promotion.requestedBy === userId;
              return (
                <TableRow key={promotion.id}>
                  <TableCell>
                    <span className="text-sm text-ink">{promotion.targetEmail}</span>
                    <p className="text-xs text-inkFaint">{promotion.reason}</p>
                  </TableCell>
                  <TableCell>
                    <span className="text-sm text-ink">{promotion.requestedByEmail}</span>
                  </TableCell>
                  <TableCell>
                    <Badge tone={STATUS_TONE[promotion.status]}>
                      {t(PROMOTION_STATUS_LABEL_KEY[promotion.status])}
                    </Badge>
                  </TableCell>
                  <TableCell>
                    <span className="text-xs text-inkFaint">
                      {new Date(promotion.expiresAt).toLocaleString('fr-TN')}
                    </span>
                  </TableCell>
                  <TableCell align="end">
                    {promotion.status === 'pending' ? (
                      <div className="flex flex-wrap items-center justify-end gap-2">
                        {mine ? (
                          <span className="text-xs text-inkFaint">
                            {t('admin.promotions.waitingForOther')}
                          </span>
                        ) : (
                          <Button
                            size="sm"
                            onClick={() => {
                              open({ kind: 'decide', promotion, decision: 'confirm' });
                            }}
                          >
                            {t('admin.promotions.action.confirm')}
                          </Button>
                        )}
                        <Button
                          variant="secondary"
                          size="sm"
                          onClick={() => {
                            open({ kind: 'decide', promotion, decision: 'reject' });
                          }}
                        >
                          {t(
                            mine
                              ? 'admin.promotions.action.cancel'
                              : 'admin.promotions.action.reject',
                          )}
                        </Button>
                      </div>
                    ) : null}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      )}

      <Modal
        open={dialog.kind !== 'closed'}
        title={dialog.kind === 'request' ? t('admin.promotions.requestTitle') : decideTitle}
        onClose={close}
        footer={
          <>
            <Button variant="secondary" onClick={close}>
              {t('merchant.common.cancel')}
            </Button>
            <Button
              onClick={() => {
                void (dialog.kind === 'request' ? onRequest() : onDecide());
              }}
              loading={requestMutation.isPending || decideMutation.isPending}
              disabled={!canSubmit}
            >
              {dialog.kind === 'request'
                ? t('admin.promotions.requestSubmit')
                : t('merchant.common.confirm')}
            </Button>
          </>
        }
      >
        {formError !== null ? (
          <p
            role="alert"
            className="mb-3 rounded-md bg-criticalSoft px-3 py-2 text-sm text-critical"
          >
            {formError}
          </p>
        ) : null}
        {dialog.kind === 'decide' && dialog.decision === 'confirm' ? (
          <p className="mb-3 rounded-md bg-surface2 px-3 py-2 text-sm text-inkSoft">
            {t('admin.promotions.confirmWarning')}
          </p>
        ) : null}
        {dialog.kind === 'decide' ? (
          <p className="mb-3 text-sm text-ink">{dialog.promotion.targetEmail}</p>
        ) : null}

        <div className="flex flex-col gap-4">
          {dialog.kind === 'request' ? (
            <Field label={t('admin.promotions.targetLabel')}>
              {(bind) => (
                <Select
                  {...bind}
                  placeholder={t('admin.promotions.targetPlaceholder')}
                  options={candidates.map((account) => ({
                    value: account.userId,
                    label: `${account.displayName ?? account.email} (${account.email})`,
                  }))}
                  value={target}
                  onChange={(event) => {
                    setTarget(event.target.value);
                  }}
                />
              )}
            </Field>
          ) : null}
          <Field label={t('admin.common.reasonLabel')}>
            {(bind) => (
              <textarea
                {...bind}
                value={motive}
                onChange={(event) => {
                  setMotive(event.target.value);
                }}
                rows={3}
                maxLength={500}
                className="ecom-input"
              />
            )}
          </Field>
          <ConfirmPasswordField value={password} onChange={setPassword} />
        </div>
      </Modal>
    </div>
  );
}
